import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, TrendingUp, CheckCircle, Filter, Calendar, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import tulioIcon from './assets/tulio.png'
import Navigation from './Navigation';
const CaseStudiesPage = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  const caseStudies = [
    {
      id: 'bigfoot-logistics',
      title: "Bigfoot Logistics",
      subtitle: "Fleet Optimization & Predictive Maintenance",
      industry: "Logistics",
      challenge: "Frequent breakdowns, unplanned downtime, and manual scheduling inefficiencies across large delivery fleet affecting customer satisfaction and operational costs.",
      solution: "Built predictive analytics engine using IoT sensors, data pipelines, and ML models with logistics dashboard and mobile app for real-time fleet management.",
      results: ["40% reduction in downtime", "25% decrease in maintenance costs", "30% improvement in scheduling efficiency", "95% customer satisfaction increase"],
      technologies: ["IoT Sensors", "Machine Learning", "Real-time Analytics", "Mobile App", "Cloud Infrastructure"],
      timeline: "16 weeks",
      teamSize: "8 engineers",
      image: "logistics-dashboard.svg",
      featured: true
    },
    {
      id: 'cristofori-music',
      title: "Cristofori Music",
      subtitle: "Retail & ERP Modernization",
      industry: "Retail",
      challenge: "Multiple legacy systems with disconnected data and poor reporting across 50+ outlet locations, leading to inventory mismanagement and operational inefficiencies.",
      solution: "Created integrated ERP stack with inventory, POS, HR, and financial modules plus real-time management dashboards and supplier network integrations.",
      results: ["20% reduction in stock-outs", "Real-time reporting across all locations", "50+ locations synchronized", "30% faster inventory turnover"],
      technologies: ["ERP Integration", "POS Systems", "Real-time Dashboards", "API Integration", "Cloud Migration"],
      timeline: "20 weeks",
      teamSize: "12 engineers",
      image: "retail-erp.svg",
      featured: true
    },
    {
      id: 'artc-industrial',
      title: "ARTC (A*STAR)",
      subtitle: "Industrial Monitoring System",
      industry: "Industrial",
      challenge: "Needed to monitor multiple industrial machines, capture sensor data, and detect anomalies in real-time to prevent costly equipment failures.",
      solution: "Architected full IoT + cloud solution with edge devices, streaming pipelines, ML anomaly detection, and visual dashboards for proactive maintenance.",
      results: ["Early fault detection implemented", "60% reduction in equipment downtime", "Preventive maintenance scheduling", "Real-time anomaly alerts"],
      technologies: ["IoT Edge Computing", "Stream Processing", "Anomaly Detection", "Cloud Infrastructure", "Real-time Dashboards"],
      timeline: "14 weeks",
      teamSize: "6 engineers",
      image: "industrial-iot.svg",
      featured: false
    },
    {
      id: 'fintech-ai-platform',
      title: "FinTech AI Assistant",
      subtitle: "Intelligent Customer Service Automation",
      industry: "Finance",
      challenge: "High volume of customer inquiries overwhelming support team, leading to long response times and decreased customer satisfaction in competitive fintech market.",
      solution: "Developed AI-powered customer service platform with natural language processing, automated ticket routing, and intelligent response generation.",
      results: ["80% reduction in response time", "70% of queries automated", "90% customer satisfaction", "50% cost reduction in support operations"],
      technologies: ["Natural Language Processing", "AI Chatbots", "Machine Learning", "API Integration", "Analytics Dashboard"],
      timeline: "12 weeks",
      teamSize: "5 engineers",
      image: "fintech-ai.svg",
      featured: true
    },
    {
      id: 'healthcare-data-platform',
      title: "Healthcare Analytics Platform",
      subtitle: "Patient Data Integration & Analytics",
      industry: "Healthcare",
      challenge: "Fragmented patient data across multiple systems making it difficult to provide comprehensive care and generate meaningful insights for treatment optimization.",
      solution: "Built secure healthcare data platform with HIPAA compliance, real-time analytics, and predictive modeling for patient outcome improvement.",
      results: ["Unified patient records", "30% improvement in diagnosis accuracy", "Real-time health monitoring", "Compliance with healthcare regulations"],
      technologies: ["Healthcare Data Integration", "HIPAA Compliance", "Predictive Analytics", "Real-time Monitoring", "Secure Cloud Infrastructure"],
      timeline: "18 weeks",
      teamSize: "10 engineers",
      image: "healthcare-platform.svg",
      featured: false
    },
    {
      id: 'ecommerce-optimization',
      title: "E-commerce Platform Scaling",
      subtitle: "High-Performance Online Marketplace",
      industry: "E-commerce",
      challenge: "Rapidly growing online marketplace experiencing performance bottlenecks, slow page loads, and system crashes during peak traffic periods affecting sales.",
      solution: "Redesigned architecture with microservices, implemented caching strategies, database optimization, and auto-scaling infrastructure for peak performance.",
      results: ["99.9% uptime achieved", "70% faster page load times", "5x traffic capacity", "25% increase in conversion rates"],
      technologies: ["Microservices Architecture", "Auto-scaling", "Database Optimization", "CDN Implementation", "Performance Monitoring"],
      timeline: "22 weeks",
      teamSize: "15 engineers",
      image: "ecommerce-platform.svg",
      featured: true
    }
  ];

  const industries = ['all', 'Logistics', 'Retail', 'Industrial', 'Finance', 'Healthcare', 'E-commerce'];

  const filteredCaseStudies = selectedIndustry === 'all' 
    ? caseStudies 
    : caseStudies.filter(study => study.industry === selectedIndustry);

  const featuredCaseStudies = caseStudies.filter(study => study.featured);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
     {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
             <Link to="/" className="flex items-center space-x-3">
            <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <Navigation />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Success Stories</Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Proven Results Across Industries
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-12">
            See how we've helped organizations transform their operations, increase efficiency, and achieve measurable growth through innovative technology solutions.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Discuss Your Project
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline">
                View Our Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Case Studies */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Featured Success Stories</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {featuredCaseStudies.slice(0, 2).map((study) => (
              <Card key={study.id} className="group hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-emerald-200 overflow-hidden">
                <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center relative">
                  <div className="text-6xl opacity-20">📊</div>
                  <Badge className="absolute top-4 left-4 bg-emerald-600 text-white">
                    {study.industry}
                  </Badge>
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{study.title}</h3>
                  <h4 className="text-lg font-semibold text-emerald-700 mb-4">{study.subtitle}</h4>
                  <p className="text-slate-600 mb-6 line-clamp-3">{study.challenge}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                    <div>
                      <span className="text-slate-500">Timeline:</span>
                      <p className="font-semibold">{study.timeline}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Team Size:</span>
                      <p className="font-semibold">{study.teamSize}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-semibold mb-3">Key Results:</h5>
                    <div className="grid grid-cols-1 gap-2">
                      {study.results.slice(0, 2).map((result, idx) => (
                        <div key={idx} className="flex items-center text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                          <span className="text-slate-600">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Link to={`/case-studies/${study.id}`}>
                    <Button className="w-full bg-slate-900 hover:bg-slate-800 group">
                      Read Full Case Study
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-4 md:mb-0">All Case Studies</h2>
            
    

          </div>
               <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">

  <div className="flex flex-wrap gap-3">
    {industries.map((industry) => (
      <button
        key={industry}
        onClick={() => setSelectedIndustry(industry)}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
          selectedIndustry === industry
            ? "bg-emerald-600 text-white border-emerald-600 shadow-md"
            : "bg-white text-slate-700 border-slate-300 hover:bg-emerald-50 hover:border-emerald-400"
        }`}
      >
        {industry === "all" ? "All Industries" : industry}
      </button>
    ))}
  </div>
</div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCaseStudies.map((study) => (
              <Card key={study.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-emerald-200 h-full">
                <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center relative">
                  <div className="text-4xl opacity-30">
                    {study.industry === 'Logistics' && '🚚'}
                    {study.industry === 'Retail' && '🏪'}
                    {study.industry === 'Industrial' && '🏭'}
                    {study.industry === 'Finance' && '💳'}
                    {study.industry === 'Healthcare' && '🏥'}
                    {study.industry === 'E-commerce' && '🛒'}
                  </div>
                  <Badge className="absolute top-2 right-2" variant="secondary">
                    {study.industry}
                  </Badge>
                </div>
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{study.title}</h3>
                    <h4 className="text-emerald-700 font-semibold mb-3">{study.subtitle}</h4>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{study.challenge}</p>
                    
                    <div className="mb-4">
                      <h5 className="font-semibold text-slate-800 mb-2 text-sm">Results:</h5>
                      <div className="space-y-1">
                        {study.results.slice(0, 2).map((result, idx) => (
                          <div key={idx} className="flex items-center text-xs">
                            <CheckCircle className="w-3 h-3 text-emerald-500 mr-2 flex-shrink-0" />
                            <span className="text-slate-600">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                             <Link to={`/case-studies/${study.id}`}>
                    <Button variant="ghost" className="w-full justify-between group-hover:bg-emerald-50 group-hover:text-emerald-700 transition-colors">
                      Read More
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  </div>

         
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Create Your Success Story?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve similar results and transform your business with innovative technology solutions.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Strategy Call
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10 px-8 py-4">
                Explore Our Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 mb-6 md:mb-0">
              <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
              <span>© 2025 Tulio Labs</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaseStudiesPage;